import { ChartsService } from '../charts.service';
import {
  Component,
  AfterViewInit,
  ElementRef,
  Input,
  OnChanges,
} from '@angular/core';
import { ChartComponent } from '../chart.component';
import { select, Selection } from 'd3-selection';
import { arc, pie, Arc } from 'd3-shape';
import { interpolate } from 'd3-interpolate';
import { isNullOrUndefined } from 'util';

@Component({
  selector: 'app-pie-chart-base',
  template: '',
})
export class PieChartBaseComponent
  extends ChartComponent
  implements OnChanges, AfterViewInit {
  @Input() data: any[];
  @Input() innerRadiusPrecentage = 55; // precentage
  @Input() valueName = 'value';
  @Input() labelName = 'label';
  @Input() invertedColor: boolean;
  @Input() interpolation;
  @Input() colorScale;

  private radius: number;
  private arc: Arc<any, any>;

  private pie: any;
  private animationDuration = 500;

  constructor(protected el: ElementRef, private chartsService: ChartsService) {
    super(el);
  }

  ngAfterViewInit() {
    this.drawChart();
  }

  ngOnChanges(): void {
    if (!this.data) {
      return;
    }

    this.drawChart();
  }

  drawChart() {
    this.updateChartDimentions();
    if (this.width && this.height && this.data) {
      this.radius = Math.min(this.width, this.height) / 2;
      this.initElements();
      this.drawPie();
    }
    setTimeout(() => {
      this.hoverFirstBiggestPath();
    }, this.animationDuration * 2);
  }

  private hoverFirstBiggestPath() {
    const firstBiggestPath = this.chartElement.querySelector('.arc > path');
    if (!firstBiggestPath) { return; }
    firstBiggestPath.dispatchEvent(new MouseEvent('mouseover'));
  }

  private initElements() {
    this.arc = arc()
      .outerRadius(this.radius - 10)
      .innerRadius(this.calculateInnerRadius());

    this.pie = pie()
      .sort(null)
      .padAngle(0.02)
      .value((d: any) => d[this.valueName]);

    this.svg = select(this.chartElement)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height)
      .append('g')
      .attr(
        'transform',
        'translate(' + this.width / 2 + ',' + this.height / 2 + ')'
      )
      .attr('cursor', 'pointer');
  }

  private drawPie() {
    const arcSelection = this.svg
      .selectAll('.arc')
      .data(this.pie(this.data))
      .enter()
      .append('g')
      .attr('class', 'arc');

    this.populateChart(arcSelection);
  }

  private populateChart(arcSelection: Selection<SVGGElement, {}, any, {}>) {
    const path = arcSelection
      .append('path')
      .attr('d', this.arc)
      .style('fill', this.calcColor.bind(this));

    setTimeout(() => {
      path
        .on('click', (d: any, i: number, elements: SVGPathElement[]) => {
          this.clickElement.emit({ d, i, elements });
        })
        .on('mouseover', this.onMouseEnter.bind(this))
        .on('mouseout', this.onMouseOut.bind(this));
    }, this.animationDuration);

    path
      .transition()
      .duration(this.animationDuration)
      .attrTween('d', (d: any) => {
        const i = interpolate(d.startAngle + 0.1, d.endAngle);
        return (t: number) => {
          d.endAngle = i(t);
          return this.arc(d);
        };
      });
  }

  private calcColor(d: any, index: number) {
    if (d.data.color) {
      return d.data.color;
    } else {
      const domainValue = this.invertedColor
        ? this.data.length - 1 - index
        : index;
      const color = this.chartsService.getColorScale(
        this.data.length,
        this.colorScale,
        this.interpolation
      );
      return color(domainValue);
    }
  }

  private onMouseEnter(d: any, i: number, elements: SVGPathElement[]) {
    this.mouseOverElement.emit({ d, i, elements });
    const arcOver = arc()
      .outerRadius(this.radius)
      .innerRadius(this.calculateInnerRadius());
    if (i !== 0) {
      select(elements[0]).transition().duration(500).attr('d', this.arc);
    }
    select(elements[i]).transition().duration(500).attr('d', arcOver);
  }

  private onMouseOut(d: any, i: number, elements: SVGPathElement[]) {
    this.mouseOutElement.emit({ d, i, elements });
    select(elements[i]).transition().duration(500).attr('d', this.arc);
    this.hoverFirstBiggestPath();
  }

  calculateInnerRadius() {
    if (isNullOrUndefined(this.innerRadiusPrecentage)) {
      return 0;
    } else {
      return (this.radius * this.innerRadiusPrecentage) / 100;
    }
  }
}
